import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  alpha,
  Card,
  CardContent,
  LinearProgress,
} from '@mui/material';
import {
  SentimentVerySatisfied as PromoterIcon,
  SentimentSatisfied as PassiveIcon,
  SentimentDissatisfied as DetractorIcon,
  TrendingUp as TrendIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ComposedChart,
  Area,
} from 'recharts';

const colors = {
  promoter: '#107C10',
  passive: '#FFB900',
  detractor: '#D13438',
  primary: '#0078D4',
  purple: '#5C2D91',
  chart: ['#0078D4', '#40E0D0', '#107C10', '#FFB900', '#5C2D91', '#D13438'],
};

const npsData = {
  promoters: 28,
  passives: 9,
  detractors: 5,
  score: 54.8, // (28-5)/42 * 100
};

const npsDistribution = [
  { score: '0-6', count: 5, type: 'Detractor' },
  { score: '7-8', count: 9, type: 'Passive' },
  { score: '9-10', count: 28, type: 'Promoter' },
];

const npsTrend = [
  { class: 'Class 8', nps: 42 },
  { class: 'Class 9', nps: 48 },
  { class: 'Class 10', nps: 51 },
  { class: 'Class 11', nps: 49 },
  { class: 'Class 12', nps: 55 },
];

const topicSatisfaction = [
  { topic: 'Content Quality', score: 88, fullMark: 100 },
  { topic: 'Instructor Clarity', score: 92, fullMark: 100 },
  { topic: 'Pace', score: 75, fullMark: 100 },
  { topic: 'Examples', score: 85, fullMark: 100 },
  { topic: 'Interaction', score: 78, fullMark: 100 },
  { topic: 'Materials', score: 82, fullMark: 100 },
];

const feedbackSentiment = [
  { category: 'Very Positive', count: 18, color: colors.promoter },
  { category: 'Positive', count: 12, color: alpha(colors.promoter, 0.6) },
  { category: 'Neutral', count: 7, color: colors.passive },
  { category: 'Negative', count: 3, color: colors.detractor },
  { category: 'Very Negative', count: 2, color: alpha(colors.detractor, 0.8) },
];

const improvementSuggestions = [
  { suggestion: 'More hands-on examples', count: 14 },
  { suggestion: 'Slower pace on complex topics', count: 11 },
  { suggestion: 'Additional resources', count: 9 },
  { suggestion: 'More Q&A time', count: 7 },
  { suggestion: 'Breakout activities', count: 6 },
];

const cohortComparison = [
  { cohort: 'Cohort A', nps: 52, csat: 85, completion: 92 },
  { cohort: 'Cohort B', nps: 55, csat: 88, completion: 90 },
  { cohort: 'Cohort C', nps: 48, csat: 82, completion: 88 },
  { cohort: 'Current', nps: 55, csat: 87, completion: 91 },
];

const NPSCustomerExperience = () => {
  return (
    <Box sx={{ p: 3, minHeight: '100vh', background: '#f5f7fa' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          📊 NPS & Customer Experience
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Chip label="Marketing Analytics 101" color="primary" size="small" />
          <Chip label="42 responses" variant="outlined" size="small" />
          <Chip label="100% response rate" sx={{ bgcolor: colors.promoter, color: 'white' }} size="small" />
        </Box>
      </Box>

      {/* NPS Score Card */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ background: `linear-gradient(135deg, ${colors.promoter} 0%, ${alpha(colors.promoter, 0.7)} 100%)`, color: 'white' }}>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h2" sx={{ fontWeight: 800, mb: 1 }}>
                {npsData.score}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600, opacity: 0.9 }}>
                Net Promoter Score
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 3 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>{npsData.promoters}</Typography>
                  <Typography variant="caption">Promoters</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>{npsData.passives}</Typography>
                  <Typography variant="caption">Passives</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>{npsData.detractors}</Typography>
                  <Typography variant="caption">Detractors</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Grid container spacing={2} sx={{ height: '100%' }}>
            <Grid item xs={12} sm={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PromoterIcon sx={{ fontSize: 32, color: colors.promoter, mr: 1 }} />
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 700 }}>66.7%</Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>Promoters (9-10)</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PassiveIcon sx={{ fontSize: 32, color: colors.passive, mr: 1 }} />
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 700 }}>21.4%</Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>Passives (7-8)</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <DetractorIcon sx={{ fontSize: 32, color: colors.detractor, mr: 1 }} />
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 700 }}>11.9%</Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>Detractors (0-6)</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              NPS Distribution
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={npsDistribution}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="score" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                  {npsDistribution.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={
                        entry.type === 'Promoter' ? colors.promoter :
                        entry.type === 'Passive' ? colors.passive :
                        colors.detractor
                      }
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              NPS Trend (Last 5 Classes)
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={npsTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="class" />
                <YAxis domain={[0, 100]} />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="nps"
                  fill={alpha(colors.promoter, 0.2)}
                  stroke={colors.promoter}
                  strokeWidth={0}
                />
                <Line
                  type="monotone"
                  dataKey="nps"
                  stroke={colors.promoter}
                  strokeWidth={3}
                  dot={{ r: 5, fill: colors.promoter }}
                  name="NPS Score"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* More Charts */}
      <Grid container spacing={3}>
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Topic Satisfaction Radar
            </Typography>
            <ResponsiveContainer width="100%" height={350}>
              <RadarChart data={topicSatisfaction}>
                <PolarGrid stroke={alpha(colors.primary, 0.3)} />
                <PolarAngleAxis dataKey="topic" tick={{ fontSize: 11 }} />
                <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                <Radar
                  name="Satisfaction Score"
                  dataKey="score"
                  stroke={colors.primary}
                  fill={colors.primary}
                  fillOpacity={0.6}
                  strokeWidth={2}
                />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Improvement Suggestions
            </Typography>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={improvementSuggestions} layout="horizontal">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis type="category" dataKey="suggestion" width={180} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" fill={colors.purple} name="Mentions" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default NPSCustomerExperience;

